import React, {useContext, useState} from 'react';

import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import LoadingButton from '@mui/lab/LoadingButton';
import MenuItem from '@mui/material/MenuItem';
import HistoryIcon from '@mui/icons-material/History';
import EditNoteIcon from '@mui/icons-material/EditNote';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import DoneIcon from '@mui/icons-material/Done';
import ClearIcon from '@mui/icons-material/Clear';
import BlockIcon from '@mui/icons-material/Block';

import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";

import {OAuthContext} from "../../../../context/OAuthContext";
import {ApplicationsTableContext} from "../../../../context/ApplicationsTableContext";
import {UserContext} from "../../../../context/UserContext";

import ApplicationHistory from "../ApplicationHistory";
import ActionModal from "./ActionModal";
import HeaderBtnBadge from "./HeaderBtnBadge";
import RequestResultSnackbar from "../../RequestResultSnackbar/RequestResultSnackbar";
import ConfirmDialog from "./ConfirmDialog";
import HeaderActionButton from "./HeaderActionButton";

import {ApplicationDetailsType, ApplicationHistoryType, OpenActionModalType} from "../types";
import FetchHelper from "../../../Utils/helper";
import {checkIfTokenExpired, formatDateToLocale} from "../../utils";
import {APPLICATION_EDITOR_INQUIRY, finishStatuses, statusesToAccept} from "../../../Utils/const";
import Status from "../../../Utils/Status";
import Alert from "@mui/material/Alert";

type DetailsHeaderProps = {
    applicationDetails: ApplicationDetailsType
    type?: string
    applicationId: string | undefined
    openModal: OpenActionModalType
    setOpenModal: (prevState: any) => void
    setApplicationDetails: (prevState: any) => void
    editorIsMe: boolean
    setEditorIsMe: (val: boolean) => void
}

const DetailsHeader: React.FC<DetailsHeaderProps> = (props) => {
    const {
        applicationDetails,
        // type,
        applicationId,
        openModal,
        setOpenModal,
        setApplicationDetails,
        editorIsMe,
        setEditorIsMe
    } = props;

    const {
        applicationNumber,
        editorNote,
        editor,
        status,
        editorInquiries,
        lastInquiredAt,
        editorExplanation,
        recruiterSignatureDate,
    } = applicationDetails;

    let hasNote = !!editorNote;

    const {oauthData} = useContext(OAuthContext);
    const {editorsList} = useContext(ApplicationsTableContext);
    const {setUserData, setErrorUser} = useContext(UserContext);
    const helper = new FetchHelper(oauthData.token);
    const jwtData = oauthData.getJwtData();
    const {t} = useTranslation('common');
    const isTokenExpired = oauthData?.isTokenExpired();

    // const [applicationType, setApplicationType] = useState<string>(type ?? "");
    const [history, setHistory] = useState<Array<ApplicationHistoryType>>([]);
    const [loading, setLoading] = useState<{
        addNote: boolean,
        decline: boolean,
        query: boolean,
        accept: boolean,
        abort: boolean,
        changeEditor: boolean,
        history: boolean
    }>({
        addNote: false,
        decline: false,
        query: false,
        accept: false,
        abort: false,
        changeEditor: false,
        history: false
    });
    const [actionValue, setActionValue] = useState<{ note: string, decline: string, query: string, accept: string }>({
        note: editorNote ? editorNote : "",
        decline: "",
        query: "",
        accept: ""
    });

    const [requestResult, setRequestResult] = useState<{
        open: boolean,
        type: "success" | "error" | undefined,
        message: string
    }>({
        open: false,
        type: undefined,
        message: ""
    });
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean,
        content: string,
        type: "abort" | "transfer" | undefined,
        title: string
    }>({
        open: false,
        content: "",
        type: undefined,
        title: ""
    })
    const [isTokenExpireSnackbar, setIsTokenExpireSnackbar] = useState<{ open: boolean, message: string }>({
        open: false,
        message: ""
    })
    const [questions, setQuestions] = React.useState<string[]>([]);

    const handleSetNote = () => {
        setLoading(prevState => ({...prevState, addNote: true}));

        handleIsTokenExpiredSnackbarStatus();
        if (applicationId && (editorNote === null || editorNote === undefined)) {
            helper.addEditorNote(parseInt(applicationId), {editorNote: actionValue.note}).then(res => {
                setApplicationDetails(res);
                handleSuccessResponseStatus(t("note_added"));
                setOpenModal((prevState: OpenActionModalType) => ({...prevState, note: false}));
            }).catch(_ => {
                handleErrorResponseStatus(t("adding_note_failed"));
            }).finally(
                () => setTimeout(() => setLoading(prevState => ({...prevState, addNote: false})), 300)
            );

        } else if (applicationId) {
            helper.editEditorNote(parseInt(applicationId!), {editorNote: actionValue.note}).then(res => {
                setApplicationDetails(res);
                handleSuccessResponseStatus(t("editing_note_successful"));
                setOpenModal((prevState: OpenActionModalType) => ({...prevState, note: false}));
            }).catch(_ => {
                handleErrorResponseStatus(t("editing_note_failed"));
            }).finally(
                () => setTimeout(() => setLoading(prevState => ({...prevState, addNote: false})), 300)
            );
        }
    }

    // const handleSendDecline = () => {
    //     setLoading(prevState => ({...prevState, decline: true}))
    //     helper.declineApplication(parseInt(applicationId!), {editorExplanation: actionValue.decline}).then(res => {
    //         setApplicationDetails(res);
    //         handleSuccessResponseStatus(t("application_declined"));
    //         setLoading(prevState => ({...prevState, decline: false}));
    //         setOpenModal((prevState: OpenActionModalType) => ({...prevState, decline: !prevState.decline}));
    //     }).catch(_ => {
    //         setLoading(prevState => ({...prevState, decline: false}));
    //         handleErrorResponseStatus(t("application_declined_failed"));
    //         setOpenModal((prevState: OpenActionModalType) => ({...prevState, decline: !prevState.decline}));
    //     })
    // }

    const transferApplication = () => {
        setLoading(prevState => ({...prevState, accept: true}));

        helper.transferApplication(parseInt(applicationId!)).then(res => {
            setApplicationDetails(res);
            updateDashboardInfo();
            handleSuccessResponseStatus(t("application_transfer_success"));
            handleCloseConfirmDialog();
        }).catch(_ => {
            handleErrorResponseStatus(t("application_transfer_failed"));
            // handleCloseConfirmDialog();
        }).finally(
            () => setTimeout(() => setLoading(prevState => ({...prevState, accept: false})), 300)
        );
    }

    const abortApplication = () => {
        setLoading(prevState => ({...prevState, abort: true}));

        helper.abortApplication(parseInt(applicationId!), {editorExplanation: null}).then(res => {
            setApplicationDetails(res);
            updateDashboardInfo();
            handleSuccessResponseStatus(t("application_abort_successful"));
            handleCloseConfirmDialog();
        }).catch(_ => {
            handleErrorResponseStatus(t("application_abort_failed"));
        }).finally(
            () => setTimeout(() => setLoading(prevState => ({...prevState, abort: false})), 300)
        );
    }

    const handleConfirmDialog = () => {
        handleIsTokenExpiredSnackbarStatus();
        if (confirmDialog.type === "abort") {
            abortApplication();
            return;
        }
        transferApplication();
    }

    const handleCloseConfirmDialog = () => {
        setConfirmDialog(prevState => ({...prevState, open: false}));
        // setLoading(prevState => ({...prevState, accept: false, abort: false}));
    }

    //TODO: CHANGE APPLICATION TYPE
    // const handleChangeApplicationType = ({target: {value}}: { target: { value: string } }) => {
    //     setApplicationType(value);
    // }

    const handleChangeEditor = ({target: {value}}: { target: { value: string } }) => {
        setLoading(prevState => ({...prevState, changeEditor: true}));
        handleIsTokenExpiredSnackbarStatus();

        const newEditor = editorsList.filter(editor => editor.employeeId.toString() === value.toString());

        if (newEditor.length !== 0) {
            helper.changeApplicationEditor(parseInt(applicationId!), {editor: newEditor[0].id}).then(res => {
                res.editor.employeeId.toString() === jwtData.sub ? setEditorIsMe(true) : setEditorIsMe(false)
                setApplicationDetails(res)
                updateDashboardInfo();
                handleSuccessResponseStatus(t("editor_changed_success"));

            }).catch(_ => {
                handleErrorResponseStatus(t("editor_changed_failed"))
            }).finally(
                () => setTimeout(() => setLoading(prevState => ({...prevState, changeEditor: false})), 300)
            );

        }
    }

    const handleAssignToMe = () => {
        setLoading(prevState => ({...prevState, changeEditor: true}));
        handleIsTokenExpiredSnackbarStatus();

        const iAmEditor = editorsList.filter(editor => editor.employeeId.toString() === jwtData.sub);

        if (iAmEditor.length !== 0) {
            setLoading(prevState => ({...prevState, changeEditor: true}))
            helper.changeApplicationEditor(parseInt(applicationId!), {editor: iAmEditor[0].id}).then(res => {
                setApplicationDetails(res);
                updateDashboardInfo();
                handleSuccessResponseStatus(t("editor_changed_success"))
            }).catch(_ => {
                handleErrorResponseStatus(t("editor_changed_failed"))
            }).finally(
                () => setTimeout(() => setLoading(prevState => ({...prevState, changeEditor: false})), 300)
            );
        }
    }

    const getHistory = () => {
        setLoading(prevState => ({...prevState, history: true}));
        handleIsTokenExpiredSnackbarStatus();

        helper.getApplicationHistory(parseInt(applicationId!)).then(res => {
            setHistory(res);
            setOpenModal((prevState: OpenActionModalType) => ({...prevState, history: true}));
        }).catch(_ => {
            handleErrorResponseStatus(t("fetch_history_failed"));
        }).finally(() => {
            setLoading(prevState => ({...prevState, history: false}));
        })
    }

    const handelNoteDialog = () => {
        handleIsTokenExpiredSnackbarStatus();
        setOpenModal((prevState: OpenActionModalType) => ({
            ...prevState,
            note: true
        }))
    }

    const handleTransferOpenDialog = () => {
        // setLoading(prevState => ({...prevState, accept: true}))

        handleIsTokenExpiredSnackbarStatus();
        setConfirmDialog(prevState => ({
            ...prevState,
            type: "transfer",
            open: true,
            content: t("application_confirm_transfer"),
            title: `Antrag ${applicationNumber} zu übertragen.`
        }))

    }

    const handleAbortOpenDialog = () => {
        // setLoading(prevState => ({...prevState, abort: true}))
        handleIsTokenExpiredSnackbarStatus();
        setConfirmDialog(prevState => ({
            ...prevState,
            type: "abort",
            open: true,
            content: t("application_confirm_abort"),
            title: `Antrag ${applicationNumber} abbrechen.`
        }))
    }

    //helpers here

    const handleCloseErrorModal = () => {
        setRequestResult(prevState => ({...prevState, open: false}))
    }

    const formatDate = () => recruiterSignatureDate ? formatDateToLocale(recruiterSignatureDate, oauthData) : "";

    const handleErrorResponseStatus = (message: string) => {
        setRequestResult({open: true, type: "error", message: message})
    }

    const handleSuccessResponseStatus = (message: string) => {
        setRequestResult({open: true, type: "success", message: message})
    }

    const handleCloseHistory = () => {
        setOpenModal((prevState: OpenActionModalType) => ({...prevState, history: false}))
        setLoading(prevState => ({...prevState, history: false}))
    }

    const updateDashboardInfo = () => {
        helper.getUserData().then(res => setUserData({...res})).catch(error => setErrorUser(prevState => ({
            ...prevState,
            error: true,
            message: error.message
        })))
    }

    const handleIsTokenExpiredSnackbarStatus = () => {
        if (checkIfTokenExpired(oauthData, isTokenExpired)) {
            setIsTokenExpireSnackbar(prevState => ({...prevState, open: true, message: t("token_expired")}))
            return;
        }
    }

    const handleQuestions = (e: React.ChangeEvent<HTMLInputElement>) => {
        let oldQuestions = questions;
        let index = oldQuestions.indexOf(e.target.value);
        if (index !== -1) {
            oldQuestions.splice(index, 1);
        }
        let newQuestion = [...oldQuestions, e.target.value];
        setQuestions(newQuestion);
    };

    const handleSendQuery = () => {
        if (questions.length === 0) {
            return false;
        }

        setLoading(prevState => ({...prevState, query: true}));

        helper.sendApplicationQuestions(parseInt(applicationId!), {editorInquiries: questions}).then(res => {
            setApplicationDetails(res)
            handleSuccessResponseStatus(t("question_send"))
            setOpenModal((prevState: OpenActionModalType) => ({...prevState, query: false}));
            setQuestions([]);
        }).catch(_ => {
            handleErrorResponseStatus(t("question_failed"))
        }).finally(
            () => setTimeout(() => setLoading(prevState => ({...prevState, query: false})), 300)
        );
    }

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={6}>
                    <Typography variant="h6">
                        {t("application_number")} {applicationNumber} {recruiterSignatureDate ? "/ " + formatDate() : ""} {applicationDetails?.mandate ? "/ " + t(`mandate_${applicationDetails.mandate}`) : ""}
                    </Typography>
                    {applicationDetails && applicationDetails.type !== "new" &&
                        <Alert variant={"standard"} severity={"warning"}>
                            {t(`application_status_${applicationDetails.type}`)}&nbsp;<b>{applicationDetails?.recruitOriginalCustomerId ?? ""}</b>
                        </Alert>}
                    {
                        lastInquiredAt &&
                        <Typography variant={"caption"}>
                            {
                                (editorInquiries && editorInquiries?.length >= 0) ?
                                    <HeaderBtnBadge tooltip={`${editorInquiries?.length} offene Rückfragen`}>
                                        Der Antrag hat Rückfragen! Gestellt
                                        am: {formatDateToLocale(lastInquiredAt, oauthData)} &nbsp;&nbsp;
                                    </HeaderBtnBadge>
                                    : `Der Antrag hatte Rückfragen! Gestellt am: ${formatDateToLocale(lastInquiredAt, oauthData)}`
                            }
                        </Typography>
                    }

                </Grid>

                <Grid item xs={6} container justifyContent="flex-end" alignItems="start" columnSpacing={2}>
                    <Grid item>
                        <HeaderActionButton loading={loading.history} disabled={loading.history} bgColor="#e1f5fe"
                                            handleAction={getHistory} label={t("show_history")}
                                            icon={<HistoryIcon/>}
                        />
                    </Grid>
                    <Grid item>
                        <HeaderActionButton loading={loading.addNote}
                                            disabled={loading.addNote}
                                            bgColor={!loading.addNote && hasNote ? "lightgreen" : "#eeeeee"}
                                            handleAction={handelNoteDialog}
                                            icon={<EditNoteIcon/>}
                                            label={t("add_note")}/>
                    </Grid>
                    {!finishStatuses.includes(status)
                        ? <>
                            <Grid item>
                                <HeaderActionButton disabled={!editorIsMe}
                                                    loading={false}
                                                    bgColor={editorIsMe ? "#e1f5fe" : "#eeeeee"}
                                                    label={t("send_query")}
                                                    icon={<QuestionAnswerIcon/>}
                                                    handleAction={() => setOpenModal((prevState: OpenActionModalType) => ({
                                                        ...prevState,
                                                        query: true
                                                    }))}/>
                            </Grid>

                            <Grid item>
                                <HeaderActionButton loading={loading.accept}
                                                    disabled={!editorIsMe || !statusesToAccept.includes(status) || loading.accept}
                                                    bgColor={editorIsMe && statusesToAccept.includes(status) && !loading.accept ? "#e8f5e9" : "#eeeeee"}
                                                    handleAction={handleTransferOpenDialog}
                                                    icon={<DoneIcon/>}
                                                    label={t("transfer")}/>
                            </Grid>
                            <Grid item>
                                <HeaderActionButton loading={loading.abort}
                                                    disabled={!editorIsMe || loading.abort}
                                                    bgColor={editorIsMe && !loading.abort ? "#fffde7" : "#eeeeee"}
                                                    handleAction={handleAbortOpenDialog}
                                                    icon={<ClearIcon/>}
                                                    label={t("cancel")}/>
                            </Grid>
                            <Grid item>
                                <HeaderActionButton disabled={true}
                                    // disabled={!editorIsMe}
                                                    loading={false}
                                                    bgColor={"rgb(253, 237, 237)"}
                                                    label={t("decline")}
                                                    icon={<BlockIcon/>}
                                    // backgroundcolor={editorIsMe ? "#fbe9e7" : "#eeeeee"}
                                                    handleAction={() => setOpenModal((prevState: OpenActionModalType) => ({
                                                        ...prevState,
                                                        decline: true
                                                    }))}></HeaderActionButton>
                            </Grid>
                        </>
                        : <Grid item><Status status={status} text={editorExplanation ?? ""}/></Grid>}
                </Grid>

                <Grid item xs={12} display="flex" justifyContent="flex-end" alignItems="start"
                      flexDirection={{xs: "column", xl: "row"}}>

                    {/*<Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap" mb={1} mr={1}>*/}
                    {/*    <FormControl variant="standard"*/}
                    {/*                 sx={{minWidth: 120, display: "flex", flexDirection: "row"}}>*/}
                    {/*        <Typography variant="subtitle1" sx={{mr: 1}}>{t("application_type")}:</Typography>*/}
                    {/*        <Select*/}
                    {/*            id="application-type"*/}
                    {/*            value={applicationType}*/}
                    {/*            onChange={(event) => handleChangeApplicationType(event)}*/}
                    {/*            disabled*/}
                    {/*        >*/}
                    {/*            <MenuItem value={applicationType}>*/}
                    {/*                {applicationType}*/}
                    {/*            </MenuItem>*/}
                    {/*        </Select>*/}
                    {/*    </FormControl>*/}
                    {/*</Box>*/}

                    {editorsList.length !== 0
                        ?
                        <Box display="flex" justifyContent="center" alignItems="center" flexWrap="wrap">
                            <FormControl variant="standard"
                                         sx={{minWidth: 120, display: "flex", flexDirection: "row"}}>
                                <Typography variant="subtitle1" sx={{mr: 1}}>{t("editor")}:</Typography>
                                <Select
                                    id="editors-select"
                                    displayEmpty
                                    value={editor?.employeeId?.toString() ?? ""}
                                    onChange={handleChangeEditor}
                                    disabled={finishStatuses.includes(status) || loading.changeEditor}
                                >
                                    <MenuItem value="" disabled>
                                        <em>{t("select")}</em>
                                    </MenuItem>
                                    {editorsList.map(({id, employeeId, name}) => (
                                        <MenuItem key={id} value={employeeId}>{name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <LoadingButton loading={loading.changeEditor} loadingIndicator={t("assign_me")}
                                           variant="text"
                                           color="primary" onClick={handleAssignToMe}
                                           sx={{
                                               padding: "0px",
                                               textTransform: "none",
                                               display: editorIsMe || finishStatuses.includes(status) ? "none" : "block"
                                           }}>{t("assign_me")}</LoadingButton></Box> : null}

                    {/*</Grid>*/}
                </Grid>
            </Grid>

            <ActionModal type="note" open={openModal.note}
                         handleClose={() => setOpenModal((prevState: OpenActionModalType) => ({
                             ...prevState,
                             note: false
                         }))}
                         title={`${t("note_to_application")} ${applicationNumber}`} handleAction={handleSetNote}
                         loading={loading.addNote}>
                <TextField
                    value={actionValue.note}
                    id="standard-multiline-static"
                    multiline
                    rows={10}
                    variant="standard"
                    sx={{width: "100%"}}
                    onChange={(e) => setActionValue(prevState => ({...prevState, note: e.target.value}))}
                    placeholder={t("enter_note")}
                />
            </ActionModal>

            <ActionModal
                type="query"
                open={openModal.query}
                handleClose={() => {
                    setOpenModal((prevState: OpenActionModalType) => ({
                        ...prevState,
                        query: false
                    }));
                    setQuestions([]);
                }}
                title={`${t("query_to_application")} ${applicationNumber}`} handleAction={handleSendQuery}
                loading={loading.query} submittable={questions.length > 0}
            >
                <FormControl variant="standard" sx={{width: "100%"}}>
                    <FormGroup>
                        {APPLICATION_EDITOR_INQUIRY.map((name) => (
                            <FormControlLabel key={name}
                                              control={<Checkbox value={name} name="questions"
                                                                 sx={{padding: 0.5, cursor: "pointer"}}
                                                                 onChange={(event) => handleQuestions(event)}/>}
                                              label={<Typography variant={"caption"}>{t(name)}</Typography>}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            </ActionModal>

            {/*{explanations.length !== 0 && translations && Object.entries(translations).length !== 0 ?*/}
            {/*    <ActionModal type="declined" open={openModal.decline} loading={loading.decline}*/}
            {/*                 handleClose={() => setOpenModal((prevState: OpenActionModalType) => ({*/}
            {/*                     ...prevState,*/}
            {/*                     decline: false*/}
            {/*                 }))}*/}
            {/*                 title={t("application_to_decline", {applicationNumber: applicationNumber})}*/}
            {/*                 handleAction={handleSendDecline}>*/}
            {/*        <FormControl variant="standard" sx={{width: "100%"}}>*/}
            {/*            <InputLabel id="decline-label">{t("select_reason_decline")} *</InputLabel>*/}
            {/*            <Select*/}
            {/*                labelId="decline-label"*/}
            {/*                id="decline"*/}
            {/*                value={actionValue.decline}*/}
            {/*                onChange={(e) => setActionValue(prevState => ({...prevState, decline: e.target.value}))}*/}
            {/*                label={`${t("select_reason_decline")} *`}*/}
            {/*            >*/}
            {/*                {explanations.map((explanation, idx) => (*/}
            {/*                    <MenuItem key={idx.toString()}*/}
            {/*                              value={`${translations[explanation] ?? explanation}`}>{`${translations[explanation] ?? explanation}`}</MenuItem>))}*/}
            {/*            </Select>*/}
            {/*        </FormControl>*/}
            {/*    </ActionModal> : null}*/}

            <RequestResultSnackbar anchorOrigin={{vertical: "top", horizontal: "right"}} open={requestResult.open}
                                   onClose={handleCloseErrorModal} autoHideDuration={3000} status={requestResult.type}
                                   message={requestResult.message}/>

            <ApplicationHistory title={`${t("application_history")} ${applicationNumber}`} data={history}
                                open={openModal.history} handleClose={handleCloseHistory} oauthData={oauthData}
                                loading={loading.history}/>

            <ConfirmDialog open={confirmDialog.open} handleClose={handleCloseConfirmDialog}
                           handleConfirm={handleConfirmDialog} content={confirmDialog.content}
                           loading={loading.accept || loading.abort} title={confirmDialog.title}/>
            <RequestResultSnackbar anchorOrigin={{vertical: "top", horizontal: "right"}}
                                   open={isTokenExpireSnackbar.open}
                                   onClose={() => setIsTokenExpireSnackbar(prevState => ({...prevState, open: false}))}
                                   autoHideDuration={3000} status="error"
                                   message={isTokenExpireSnackbar.message}/>
        </>
    );
};

export default DetailsHeader;